import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import client, { authClient } from 'api/apiClient';
import { notify } from 'components/Common/Toast';
import { deletePropertyPath, getDefaultContainerId } from 'utils/helpers';
import { resetState } from 'redux/action/resetState';
import { oldGloryBaseUrl } from 'config/apiConfig';


interface ProfileItem {
    key: string;
    value: string;
}

interface Address {
    id: number;
    user: any;
    company: any;
    street1: string;
    street2: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
    isDefault: boolean;
    lat: null | number;
    lon: null | number;
    addressType: string;
    addressSwitchId: null | number;
}
interface Merchant {
    name: string;
    companyCode: string;
    country: string;
    status: string;
    companyId: number;
    logoUrl: string;
    webSite: string;
    email: string;
    timezone: string;
    phone: string;
    googlePlaceId: string;
    address: string;
    lat: number;
    lon: number;
    campaign: string;
    description: string;
    customFields_2: string[]; // Assuming customFields_2 is an array of strings
    profileItems: ProfileItem[];
    default: boolean;
    city?: string;
    state?: string;
    zip?: string;
}
interface LoginInfo {
    AutoGenerated: boolean;
    Channel: string;
    CreateDate: string;
    CustomerID: number;
    CustomerName: string;
    DashboardProfile: string;
    EffectiveUserStatus: string;
    EmailAddress: string | null;
    FirstName: string;
    GroupID: number;
    GroupName: string;
    HasAuthorizationToken: boolean;
    HasPendingChanges: boolean;
    IsCompany: boolean;
    LastChange: string;
    LastFailed: string;
    LastLogon: string;
    LastName: string;
    LoginName: string;
    ManageUserRoles: boolean;
    ManageUsers: boolean;
    MiddleName: string | null;
    NumInvalidAttempts: number;
    ProfileUpdated: boolean;
    Status: string;
    UiSelectionShortName: string;
    UseCustomerAccounts: boolean;
    UserID: number;
    UserLogonID: number;
    UserStatus: string;
}

interface UserInfo {
    name: string;
    email: string;
    username: string;
    id: string | number;
    country?: string;
    firstName?: string;
    lastName?: string;
    telephone?: string;
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    zip?: number;
    svaId?: string;
    image: string;
    merchants?: Merchant[];
    parishName?: string;
    accountBalance?: string | number;
    addresses?: Address[];
    companyId?: number | null;
    businessName?: string;
    nickname?: string;
    merchantImgUrl?: string;
    merchantEmail?: string;
    companyCode?: string;
    companies?: any;
}

interface UserState {
    userInfo: UserInfo | null;
    loginInfo: LoginInfo | null;
    loading: boolean;
    error: string | null;
    updateInfoLoading: boolean;
    updateInfoError: string | null;
    defaultMerchant: null | Merchant;
    defaultCompanyId: number | null;
    defaultContainerId: number | null;
    userResponse: any;
    address: null | Address;
}

export interface ResponseOverRide extends AxiosResponse {
    accountInfo?: UserInfo | null;
    loginInfo?: LoginInfo | null;
    moneyContainerBalances?: any;
    responseData?: any;
    code?: number;
    message?: string;
    statusCode?: string;
}

const initialState: UserState = {
    userInfo: null,
    loginInfo: null,
    loading: true,
    error: null,
    updateInfoLoading: false,
    updateInfoError: null,
    defaultMerchant: null,
    defaultCompanyId: null,
    defaultContainerId: null,
    userResponse: null,
    address: null,
};

const keysToRemoveIfEmpty = ['parishName', 'address.street2', 'username'];

export const setMyPicture = createAsyncThunk('user/setMyPicture', async ({ formData }: { formData: any }, { dispatch }) => {
    const res: any = await client.post(`${oldGloryBaseUrl}/user/setImage`, formData, {
        'Content-Type': 'multipart/form-data',
    });

    if (res.code === 200) {
        notify('update picture successfully!', 'success');
        dispatch(fetchUserInfo());
    }
    return res;
});

export const setMerchantPicture = createAsyncThunk('user/setMerhantPicture', async ({ formData }: { formData: any }, { dispatch }) => {
    const res: any = await client.post(`${oldGloryBaseUrl}/merchant/setImage`, formData, {
        'Content-Type': 'multipart/form-data',
    });
    notify('update picture successfully!', 'success');

    if (res.code === 200) {
        dispatch(fetchUserInfo());
    }
    return res;
});

export const removeMyPicture = createAsyncThunk('user/removeMyPicture', async (_, { dispatch }) => {
    const res: any = await client.delete(`${oldGloryBaseUrl}/user/removeImage`);
    if (res.code === 200) {
        dispatch(fetchUserInfo());
        notify('Picture removed successfully!', 'success');
    }
    return res;
});

export const removeMerchantPicture = createAsyncThunk('user/removeMerchantPicture', async (_, { dispatch }) => {
    const res: any = await client.delete(`${oldGloryBaseUrl}/merchant/removeImage`);
    notify('Picture removed successfully!', 'success');

    if (res.code === 200) {
        dispatch(fetchUserInfo());
    }
    return res;
});

export const fetchUserInfo = (loading?: boolean) => async (dispatch: any) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    typeof loading === 'boolean' && dispatch(setUserInfoLoading(loading));
    const { payload } = await dispatch(myUserInfo());
    if (!payload) {
        notify('Error while fetching user info, Please contact support', 'error');
        dispatch(logoutUser());
    }
};

export const myUserInfo = createAsyncThunk('user/userInfo', async (_, { dispatch }: any) => {
    const response: ResponseOverRide = await client.get(`${oldGloryBaseUrl}/user/getUserInfo`);
    if (!response?.accountInfo) {
        dispatch(logoutUser());
        return;
    }
    return response;
});

//Change the keys named address1, address2, and zip to street1, street2, and zipcode, respectively,
//because the backend accepts street1, street2, and zipcode, and returns address1, address2, and zip.
const normalizeUserData = (userData: any, addressId?: number) => {
    const _userData = { ...userData };
    const { address1: street1, address2: street2, zip, ...rest } = _userData.address || {};
    const normalizedData = { ...userData, addressId, address: { ...rest, street1, street2, zipCode: zip } };
    // REMOVE THE KEYS THAT HAVE EMPTY, NULL OR UNDEFINED VALUES DUE TO BACKEND RESTRICTIONS
    keysToRemoveIfEmpty.forEach((key) => {
        if (!normalizedData?.[key]) {
            deletePropertyPath(normalizedData, key);
        }
    });
    return normalizedData;
};

export const userUpdateInfo = createAsyncThunk('user/userUpdateInfo', async (userData: any, { dispatch, getState }) => {
    const state: any = getState();
    const addressId = state?.user?.address?.id;
    const normalizedData = normalizeUserData(userData, addressId);
    const response: any = await client.post('/user/updateMyInfo', normalizedData);
    if (response?.code === 200) {
        dispatch(myUserInfo());
        notify('Information updated successfully!', 'success');
    } else if (!response) {
        notify('Someting went wrong!', 'error');
    }
    return response;
});

export const logoutUser = createAsyncThunk('user/logoutUser', async (_, { dispatch }) => {
    // do not remove this try catch as we are forcefully logging out in case of error in logout api
    try {
        await client.post('user/logout', {}, { hideToast: true });
    } catch (e) {
        console.log('logout error', e);
    } finally {
        authClient.clearTokens();
        client.clearTokens();
        dispatch(resetState());
        notify('Logged out successfully!', 'success');
    }
});

const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        setUserInfoLoading: (state, action) => {
            state.loading = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(myUserInfo.fulfilled, (state, action) => {
            state.loading = false;
            if (!action?.payload) return;
            const { accountInfo, moneyContainerBalances, loginInfo } = action?.payload || {};
            let { businessName, merchantImgUrl, merchantEmail, companyId, nickname, companyCode } = accountInfo || {};
            if (accountInfo) {
                if (accountInfo?.companies?.length) {
                    const { name, email, logoUrl, companyId: _companyId, companyCode: _companyCode } = accountInfo?.companies[0] || {};
                    businessName = name;
                    merchantEmail = email;
                    merchantImgUrl = logoUrl;
                    companyId = _companyId;
                    if (!companyCode && _companyCode) {
                        nickname = _companyCode;
                        companyCode = _companyCode;
                    }
                }
                state.defaultCompanyId = companyId || null;
                state.userInfo = { ...accountInfo, businessName, merchantImgUrl, merchantEmail, companyId, nickname, companyCode };
            }
            if (loginInfo) {
                state.loginInfo = { ...loginInfo };
            }
            state.userResponse = action?.payload;
            state.defaultContainerId = getDefaultContainerId(moneyContainerBalances);
        });
        builder.addCase(myUserInfo.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || null;
        });
    },
});
export const { setUserInfoLoading } = userSlice.actions;

export default userSlice.reducer;
